import { useEffect, useState, Fragment } from "react";
import { api } from "../api";
import { useAsync } from "../hooks";
import { RefreshButton } from "./RefreshButton";
import { fmt, short } from "../format";
import { ConfirmModal } from "./ConfirmModal";
import type { TenantSummary, TenantDetail, DeletedTenant } from "../types";

const PAGE_SIZE = 25;

/** A "client" = an owner person and the workspaces they own (drill-down). */
interface ClientGroup {
  ownerUserId: string;
  ownerEmail: string;
  workspaceCount: number;
  totalScrapers: number;
  lastActivityAt: string | null;
  workspaces: TenantSummary[];
}
interface ClientsPage {
  clients: ClientGroup[];
  total: number;
  page: number;
  pageSize: number;
}

/**
 * The client list — GROUPED BY OWNER. A "client" is the owner person; expand a row to drill into the
 * workspaces they own. Operator-only (403 at the API for anyone else). Search + paginate are server-side
 * (GET /clients?page&pageSize&q); the box is debounced. Opening a workspace fetches its detail on demand.
 */
export function Clients({ onOpenFleet }: { onOpenFleet: (tenantId: string) => void }) {
  const [open, setOpen] = useState<TenantSummary | null>(null);
  const [q, setQ] = useState("");
  const [dq, setDq] = useState(""); // debounced search sent to the server
  const [page, setPage] = useState(0);
  const [showSystem, setShowSystem] = useState(false); // reveal the DEFAULT/system tenant
  const [showCancelled, setShowCancelled] = useState(false); // reveal deleted-client tombstones
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // Debounce the search box → one refetch after typing settles; a new search resets to page 0.
  useEffect(() => {
    const t = setTimeout(() => {
      setDq(q.trim());
      setPage(0);
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const qs = new URLSearchParams({ page: String(page), pageSize: String(PAGE_SIZE) });
  if (dq) qs.set("q", dq);
  if (showSystem) qs.set("includeSystem", "1");
  const { data, loading, error, reload } = useAsync<ClientsPage>(
    () => api(`/clients?${qs.toString()}`),
    [page, dq, showSystem],
  );
  const { data: cancelledData } = useAsync<{ deleted: DeletedTenant[] }>(
    () => (showCancelled ? api("/tenants/deleted") : Promise.resolve({ deleted: [] })),
    [showCancelled],
  );
  const cancelled = cancelledData?.deleted ?? [];

  if (loading && !data) return <div className="muted pad">Loading…</div>;
  if (error) return <div className="err pad">Error: {error}</div>;
  const clients = data?.clients ?? [];
  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // While searching, auto-expand every returned client so the matching workspace is visible.
  const isExpanded = (id: string) => dq !== "" || expanded.has(id);
  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div className="stack">
      <div className="section-head">
        <h1>Clients</h1>
        <div className="row-gap">
          <input
            className="clients-search"
            placeholder="filter by owner / workspace / #id"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <label className="muted sm" style={{ display: "flex", alignItems: "center", gap: "var(--sp2)", whiteSpace: "nowrap" }}>
            <input
              type="checkbox"
              checked={showSystem}
              onChange={(e) => {
                setShowSystem(e.target.checked);
                setPage(0);
              }}
            />
            Show system accounts
          </label>
          <label className="muted sm" style={{ display: "flex", alignItems: "center", gap: "var(--sp2)", whiteSpace: "nowrap" }}>
            <input
              type="checkbox"
              checked={showCancelled}
              onChange={(e) => setShowCancelled(e.target.checked)}
            />
            Show deleted
          </label>
          <RefreshButton onClick={reload} />
        </div>
      </div>

      <p className="fleet-line lead">
        <em>{total}</em> client{total === 1 ? "" : "s"}
        {dq && (
          <>
            {" "}
            matching <em>“{dq}”</em>
          </>
        )}
      </p>

      {total === 0 && <div className="empty">{dq ? "No clients match." : "No clients yet."}</div>}

      {clients.length > 0 && (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Client</th>
                <th className="num">Workspaces</th>
                <th className="num">Scrapers</th>
                <th>Last activity</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {clients.map((c) => {
                const exp = isExpanded(c.ownerUserId);
                return (
                  <Fragment key={c.ownerUserId}>
                    <tr className="client-row" onClick={() => toggle(c.ownerUserId)}>
                      <td>
                        <span className="client-caret">{exp ? "▾" : "▸"}</span> {c.ownerEmail}
                      </td>
                      <td className="num">{c.workspaceCount}</td>
                      <td className="num">{c.totalScrapers}</td>
                      <td className="nowrap">{c.lastActivityAt ? fmt(c.lastActivityAt) : "—"}</td>
                      <td className="acts">
                        <button
                          className="btn ghost sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggle(c.ownerUserId);
                          }}
                        >
                          {exp ? "Hide" : "Workspaces"}
                        </button>
                      </td>
                    </tr>
                    {exp &&
                      c.workspaces.map((w) => (
                        <tr key={w.id} className="workspace-row">
                          <td>
                            <span className="ws-indent" />
                            {w.name}
                            <span className={w.status === "active" ? "pill" : "pill paused"} style={{ marginLeft: "var(--sp2)" }}>
                              {w.status}
                            </span>
                            {w.notJoined && (
                              <span
                                className="pill hold"
                                style={{ marginLeft: "var(--sp2)" }}
                                title="A pre-created invite — nobody has signed in to this workspace yet."
                              >
                                not signed in
                              </span>
                            )}
                            <br />
                            <span className="mono muted ws-indent-text">{short(w.id)}</span>
                          </td>
                          <td />
                          <td className="num">{w.scraperCount}</td>
                          <td className="nowrap">{w.lastActivityAt ? fmt(w.lastActivityAt) : "—"}</td>
                          <td className="acts">
                            <button className="btn ghost sm" onClick={() => setOpen(w)}>
                              Open
                            </button>
                            <button
                              className="btn ghost sm"
                              onClick={() => onOpenFleet(w.id)}
                              disabled={w.scraperCount === 0}
                            >
                              Scrapers →
                            </button>
                          </td>
                        </tr>
                      ))}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {total > PAGE_SIZE && (
        <div className="pager">
          <button className="btn ghost sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
            ← Prev
          </button>
          <span className="muted sm">
            {page * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE + PAGE_SIZE, total)} of {total}
          </span>
          <button
            className="btn ghost sm"
            disabled={page >= pageCount - 1}
            onClick={() => setPage((p) => p + 1)}
          >
            Next →
          </button>
        </div>
      )}

      {showCancelled && (
        <section style={{ marginTop: "var(--sp5)" }}>
          <div className="section-head">
            <h2>Deleted accounts</h2>
            <span className="muted sm">deleted clients — a brief record, not restorable</span>
          </div>
          {cancelled.length === 0 ? (
            <div className="empty">No deleted accounts.</div>
          ) : (
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th className="num">#</th>
                    <th>Client</th>
                    <th>Deleted</th>
                    <th>By</th>
                    <th className="num">Scrapers</th>
                    <th className="num">People</th>
                  </tr>
                </thead>
                <tbody>
                  {cancelled.map((c) => (
                    <tr key={c.id} className="muted">
                      <td className="num mono">{c.seq}</td>
                      <td>
                        {c.name}
                        <br />
                        <span className="mono muted">{short(c.id)}</span>
                      </td>
                      <td className="nowrap">{fmt(c.deletedAt)}</td>
                      <td className="mono muted">{c.deletedBy ? short(c.deletedBy) : "—"}</td>
                      <td className="num">{c.scrapers}</td>
                      <td className="num">{c.usersDeleted}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {open && (
        <ClientPanel
          summary={open}
          onClose={() => setOpen(null)}
          onChanged={reload}
          onDeleted={() => {
            setOpen(null);
            reload();
          }}
          onOpenFleet={(id) => {
            setOpen(null);
            onOpenFleet(id);
          }}
        />
      )}
    </div>
  );
}

function ClientPanel({
  summary,
  onClose,
  onChanged,
  onDeleted,
  onOpenFleet,
}: {
  summary: TenantSummary;
  onClose: () => void;
  onChanged: () => void;
  onDeleted: () => void;
  onOpenFleet: (tenantId: string) => void;
}) {
  const { data, loading, error } = useAsync<TenantDetail>(
    () => api(`/tenants/${summary.id}`),
    [summary.id],
  );
  const [status, setLocalStatus] = useState(summary.status);
  const [busy, setBusy] = useState(false);
  const [actErr, setActErr] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  async function changeStatus(next: "active" | "suspended") {
    setBusy(true);
    setActErr(null);
    try {
      await api(`/tenants/${summary.id}/status`, { method: "PATCH", body: JSON.stringify({ status: next }) });
      setLocalStatus(next);
      onChanged();
    } catch (e) {
      setActErr(e instanceof Error ? e.message : "failed to change status");
    } finally {
      setBusy(false);
    }
  }

  async function doDelete() {
    setBusy(true);
    setActErr(null);
    try {
      await api(`/tenants/${summary.id}`, { method: "DELETE", body: JSON.stringify({ confirmName: summary.name }) });
      onDeleted();
    } catch (e) {
      setActErr(e instanceof Error ? e.message : "failed to delete client");
      setBusy(false); // keep the panel open so the error is visible
    }
  }

  return (
    <>
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="section-head">
          <div>
            <h2>
              <span className="mono muted">#{summary.seq}</span> {summary.name}
            </h2>
            <p className="fleet-line" style={{ marginTop: 6 }}>
              <span className="mono">{short(summary.id)}</span> · client since{" "}
              {fmt(summary.created_at)}
            </p>
          </div>
          <div className="row-gap">
            <button className="btn ghost sm" onClick={() => onOpenFleet(summary.id)}>
              Scrapers →
            </button>
            <button className="btn ghost sm" onClick={onClose}>
              Close
            </button>
          </div>
        </div>

        {loading && <div className="muted pad">Loading…</div>}
        {error && <div className="err pad">Error: {error}</div>}
        {data && (
          <>
            <h3 className="mt">Last 24 hours</h3>
            <div className="health-bar">
              <span className="hb-item">
                <b>{data.stats.runs24h.total}</b> runs
              </span>
              {data.stats.runs24h.failed > 0 && (
                <span className="hb-item bad">
                  <b>{data.stats.runs24h.failed}</b> failed
                </span>
              )}
              {data.stats.heals24h.healed > 0 && (
                <span className="hb-item gold">
                  <b>{data.stats.heals24h.healed}</b> repaired
                </span>
              )}
              <span className="hb-item">
                <b>${data.stats.heals24h.spendUsd.toFixed(4)}</b> repair spend
              </span>
            </div>

            <h3 className="mt">Scrapers</h3>
            <div className="health-bar">
              <span className="hb-item">
                <b>{data.stats.scrapers.total}</b> total
              </span>
              <span className="hb-item">
                <b>{data.stats.scrapers.active}</b> collecting
              </span>
              {data.stats.scrapers.paused > 0 && (
                <span className="hb-item bad">
                  <b>{data.stats.scrapers.paused}</b> paused
                </span>
              )}
            </div>
            {data.scrapers.length > 0 && (
              <div className="table-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>Scraper</th>
                      <th>Status</th>
                      <th>Last run</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.scrapers.map((s) => (
                      <tr key={s.id}>
                        <td>
                          {s.name}
                          <br />
                          <span className="mono muted">{s.target_urls[0]}</span>
                        </td>
                        <td>
                          <span className={s.status === "active" ? "pill" : "pill paused"}>{s.status}</span>
                        </td>
                        <td className="nowrap">
                          {s.last_run_at ? fmt(s.last_run_at) : "—"}
                          {s.last_run_status ? (
                            <>
                              {" · "}
                              <span className={`pill ${s.last_run_status}`}>{s.last_run_status}</span>
                            </>
                          ) : (
                            ""
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <h3 className="mt">API keys</h3>
            {data.apiKeys.length === 0 ? (
              <div className="empty">No API keys.</div>
            ) : (
              <div className="table-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Key</th>
                      <th>Status</th>
                      <th>Last used</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.apiKeys.map((k) => (
                      <tr key={k.id}>
                        <td>{k.name}</td>
                        <td className="mono muted">{k.keyPrefix}…</td>
                        <td>
                          <span className={k.status === "active" ? "pill" : "pill paused"}>{k.status}</span>
                        </td>
                        <td className="nowrap">{k.lastUsedAt ? fmt(k.lastUsedAt) : "never"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <h3 className="mt">People</h3>
            {data.users.length === 0 ? (
              <div className="empty">
                Nobody has signed in to this client yet — it has no users.
              </div>
            ) : (
              <div className="table-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Signed in with Google</th>
                      <th>Since</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.users.map((u) => (
                      <tr key={u.id}>
                        <td>{u.email}</td>
                        <td>{u.googleSub ? "yes" : <span className="muted">not yet</span>}</td>
                        <td className="nowrap">{fmt(u.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* Operator controls — pause is reversible; delete is not. */}
        <div style={{ marginTop: "var(--sp5)", borderTop: "1px solid var(--rule)", paddingTop: "var(--sp4)" }}>
          {actErr && <div className="err" style={{ marginBottom: "var(--sp3)" }}>{actErr}</div>}
          <div className="row-gap">
            <button
              className="btn"
              disabled={busy}
              onClick={() => void changeStatus(status === "suspended" ? "active" : "suspended")}
            >
              {busy ? "Working…" : status === "suspended" ? "Resume client" : "Pause client"}
            </button>
            <div style={{ flex: 1 }} />
            <button className="btn danger" disabled={busy} onClick={() => setConfirmDelete(true)}>
              Delete client…
            </button>
          </div>
          <p className="note" style={{ marginTop: "var(--sp2)" }}>
            Pausing suspends the client and pauses its scrapers (reversible). Deleting permanently
            removes the client and all its data — scrapers, runs, results, keys, and people.
          </p>
        </div>
      </div>
    </div>

      {confirmDelete && (
        <ConfirmModal
          title={`Delete ${summary.name}?`}
          body="This permanently removes the client and everything it owns — scrapers, runs, results, API keys, and any people whose only workspace this is. This cannot be undone. Type the client name to confirm."
          confirmLabel="Delete client"
          danger
          busy={busy}
          confirmPhrase={summary.name}
          onCancel={() => setConfirmDelete(false)}
          onConfirm={() => void doDelete()}
        />
      )}
    </>
  );
}
